import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// Copyright 2022 The Parca Authors
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
import { useEffect, useState } from 'react';
import { Button, ButtonGroup, Card, DateTimeRange, DateTimeRangePicker, IconButton, useGrpcMetadata, } from '@parca/components';
import { CloseIcon } from '@parca/icons';
import { Query } from '@parca/parser';
import { getStepDuration, getStepDurationInMilliseconds } from '@parca/utilities';
import { MergedProfileSelection } from '..';
import MatchersInput from '../MatchersInput/index';
import { useMetricsGraphDimensions } from '../MetricsGraph/useMetricsGraphDimensions';
import ProfileMetricsGraph, { ProfileMetricsEmptyState } from '../ProfileMetricsGraph';
import ProfileTypeSelector from '../ProfileTypeSelector/index';
import CompareButton from './CompareButton';
import { useAutoQuerySelector } from './useAutoQuerySelector';
export const useProfileTypes = (client) => {
    const [result, setResult] = useState(undefined);
    const [error, setError] = useState(undefined);
    const [loading, setLoading] = useState(true);
    const metadata = useGrpcMetadata();
    useEffect(() => {
        if (!loading) {
            return;
        }
        const call = client.profileTypes({}, { meta: metadata });
        call.response
            .then(response => setResult(response))
            .catch(error => setError(error))
            .finally(() => setLoading(false));
    }, [client, metadata, loading]);
    return { loading, data: result, error };
};
const ProfileSelector = ({ queryClient, querySelection, selectProfile, selectQuery, closeProfile, enforcedProfileName, profileSelection, comparing, onCompareProfile, }) => {
    const { loading: profileTypesLoading, data: profileTypesData, error, } = useProfileTypes(queryClient);
    const { heightStyle } = useMetricsGraphDimensions();
    const [timeRangeSelection, setTimeRangeSelection] = useState(DateTimeRange.fromRangeKey(querySelection.timeSelection));
    const [queryExpressionString, setQueryExpressionString] = useState(querySelection.expression);
    useEffect(() => {
        if (enforcedProfileName !== '') {
            const [q, changed] = Query.parse(querySelection.expression).setProfileName(enforcedProfileName);
            if (changed) {
                setQueryExpressionString(q.toString());
                return;
            }
        }
        setQueryExpressionString(querySelection.expression);
    }, [enforcedProfileName, querySelection.expression]);
    const enforcedProfileNameQuery = () => {
        const pq = Query.parse(queryExpressionString);
        const [q] = pq.setProfileName(enforcedProfileName);
        return q;
    };
    const query = enforcedProfileName !== '' ? enforcedProfileNameQuery() : Query.parse(queryExpressionString);
    const selectedProfileName = query.profileName();
    const setNewQueryExpression = (expr) => {
        const query = enforcedProfileName !== '' ? enforcedProfileNameQuery() : Query.parse(expr);
        const delta = query.profileType().delta;
        const from = timeRangeSelection.getFromMs();
        const to = timeRangeSelection.getToMs();
        const mergeParams = delta
            ? {
                mergeFrom: from,
                mergeTo: to,
            }
            : {};
        selectQuery({
            expression: expr,
            from,
            to,
            timeSelection: timeRangeSelection.getRangeKey(),
            ...mergeParams,
        });
    };
    const setQueryExpression = () => {
        setNewQueryExpression(query.toString());
    };
    const addLabelMatcher = (key, value) => {
        // When a user clicks on a label on the metrics graph tooltip,
        // replace single `\` in the `value` string with doubles `\\` if available.
        const newValue = value.includes('\\') ? value.replaceAll('\\', '\\\\') : value;
        const [newQuery, changed] = Query.parse(queryExpressionString).setMatcher(key, newValue);
        if (changed) {
            // TODO: Change this to store the query object
            setNewQueryExpression(newQuery.toString());
        }
    };
    const setMatchersString = (matchers) => {
        const newExpressionString = `${selectedProfileName}{${matchers}}`;
        setQueryExpressionString(newExpressionString);
    };
    const setProfileName = (profileName) => {
        if (profileName === undefined) {
            return;
        }
        const [newQuery, changed] = query.setProfileName(profileName);
        if (changed) {
            const q = newQuery.toString();
            setQueryExpressionString(q);
        }
    };
    useAutoQuerySelector({
        selectedProfileName,
        profileTypesData,
        setProfileName,
        setQueryExpression,
    });
    const handleCompareClick = () => onCompareProfile();
    const searchDisabled = queryExpressionString === undefined ||
        queryExpressionString === '' ||
        queryExpressionString === '{}';
    const compareDisabled = selectedProfileName === '' || querySelection.expression === undefined;
    return (_jsxs(Card, { className: "overflow-visible", children: [_jsxs(Card.Header, { className: "flex !items-center space-x-2", children: [_jsxs("div", { className: "flex w-full flex-wrap items-center justify-start space-x-2 space-y-1", children: [_jsx("div", { className: "ml-2 mt-1", children: _jsx(ProfileTypeSelector, { profileTypesData: profileTypesData, loading: profileTypesLoading, selectedKey: selectedProfileName, onSelection: setProfileName, error: error }) }), _jsx("div", { className: "w-full flex-1", children: _jsx(MatchersInput, { queryClient: queryClient, setMatchersString: setMatchersString, runQuery: setQueryExpression, currentQuery: query }) }), _jsx(DateTimeRangePicker, { onRangeSelection: setTimeRangeSelection, range: timeRangeSelection }), _jsxs(ButtonGroup, { children: [!searchDisabled && (_jsx(_Fragment, { children: !comparing && (_jsx(CompareButton, { disabled: compareDisabled, onClick: handleCompareClick })) })), _jsx(Button, { disabled: searchDisabled, onClick: (e) => {
                                            e.preventDefault();
                                            setQueryExpression();
                                        }, children: "Search" })] })] }), _jsx("div", { children: comparing && _jsx(IconButton, { onClick: () => closeProfile(), icon: _jsx(CloseIcon, {}) }) })] }), _jsx(Card.Body, { children: _jsx("div", { style: { height: heightStyle }, children: querySelection.expression !== undefined &&
                        querySelection.expression.length > 0 &&
                        querySelection.from !== undefined &&
                        querySelection.to !== undefined ? (_jsx(ProfileMetricsGraph, { queryClient: queryClient, queryExpression: querySelection.expression, from: querySelection.from, to: querySelection.to, profile: profileSelection, setTimeRange: (range) => {
                            const from = range.getFromMs();
                            const to = range.getToMs();
                            let mergedProfileParams = {};
                            if (query.profileType().delta) {
                                mergedProfileParams = { mergeFrom: from, mergeTo: to };
                            }
                            setTimeRangeSelection(range);
                            selectQuery({
                                expression: queryExpressionString,
                                from,
                                to,
                                timeSelection: range.getRangeKey(),
                                ...mergedProfileParams,
                            });
                        }, addLabelMatcher: addLabelMatcher, onPointClick: (timestamp, labels, queryExpression) => {
                            // TODO: Pass the query object via click rather than queryExpression
                            let query = Query.parse(queryExpression);
                            labels.forEach(l => {
                                const [newQuery, updated] = query.setMatcher(l.name, l.value);
                                if (updated) {
                                    query = newQuery;
                                }
                            });
                            const stepDuration = getStepDuration(querySelection.from, querySelection.to);
                            const stepDurationInMilliseconds = getStepDurationInMilliseconds(stepDuration);
                            const mergeFrom = timestamp;
                            const mergeTo = query.profileType().delta
                                ? mergeFrom + stepDurationInMilliseconds
                                : mergeFrom;
                            selectProfile(new MergedProfileSelection(mergeFrom, mergeTo, query));
                        } })) : (_jsx(_Fragment, { children: profileSelection == null ? (_jsx(ProfileMetricsEmptyState, { message: `Please select a profile type and click "Search" to begin.` })) : null })) }) })] }));
};
export default ProfileSelector;
